import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useCreateCustomConversation } from 'chat/conversations/queries/create_custom'
import { useUpdateMembers } from 'chat/members/queries/update'
import React, { useCallback, useLayoutEffect } from 'react'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'
import { useUser } from 'users/queries/detail'
import { useCreateCustomGroup } from '../context'
import { CreateGroupScreenProps } from '../stack/types'
import { CreateGroupView } from './view'

export function CreateGroupScreen({
  navigation,
  route,
}: CreateGroupScreenProps) {
  const { theme } = useTheme()
  const { data: user } = useUser()
  const { handleNavigation } = useRoutingContext()
  const { members, setMembers, group, setGroup, selectedMemberAliases } =
    useCreateCustomGroup()
  const createCustomConversationMutation = useCreateCustomConversation()
  const updateMembersMutation = useUpdateMembers()
  const { companyId } = route.params || {}

  const createConversation = useCallback(async () => {
    const conversation = await createCustomConversationMutation.mutateAsync({
      group: group!,
      companyId,
    })
    await updateMembersMutation.mutateAsync({
      conversationId: conversation.id,
      members: Object.values(members),
      memberAliases: selectedMemberAliases ?? [],
    })

    handleNavigation({
      routeName: 'conversations',
      routeParams: {
        screen:
          user?.role === 'observer' || user?.role === 'admin'
            ? 'groups'
            : 'allConversations',
      },
    })
    if (conversation.type === 'repository') {
      handleNavigation({
        routeName: 'file_folders_bottom_tab',
        routeParams: {
          screen: 'files',
          params: { conversationId: conversation.id },
          conversationId: conversation.id,
        },
      })
    } else {
      handleNavigation({
        routeName: 'messages',
        routeParams: { id: conversation.id },
      })
    }
  }, [
    createCustomConversationMutation,
    group,
    companyId,
    members,
    updateMembersMutation,
    members,
    selectedMemberAliases,
    handleNavigation,
    user?.role,
  ])

  const canSave = group && group.name.length > 0

  useLayoutEffect(() => {
    showHeaderButton({
      navigation: navigation as any,
      theme,
      title: t('create'),
      onPress: createConversation,
      position: 'right',
      disabled: !canSave || createCustomConversationMutation.isPending,
    })
  }, [
    canSave,
    createConversation,
    createCustomConversationMutation.isPending,
    navigation,
    theme,
  ])

  return (
    <StatusBoundary
      isLoading={
        createCustomConversationMutation.isPending ||
        updateMembersMutation.isPending
      }
      error={
        createCustomConversationMutation.error ?? updateMembersMutation.error
      }
      loadingMessage={t('saving')}
    >
      <CreateGroupView
        user={user}
        members={members}
        group={group}
        onGroupChanged={setGroup}
        onMembersChanged={setMembers}
        selectedMemberAliases={selectedMemberAliases}
      />
    </StatusBoundary>
  )
}
